import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subject, Subscription, interval, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import {
  CalculatorCountry,
  CalculatorEstimateResult,
  CalculatorFormField,
  CalculatorFormOption,
  CalculatorFormSchema,
  CalculatorVariant,
  CalculatorVisibilityRule,
  VitaCalculatorService,
  isBeneficiaryField,
} from '../../services/vita-calculator.service';
import {
  VitaBeneficiario,
  VitaBeneficiarioService,
} from '../../services/vita-beneficiario.service';
import { AddVitaBeneficiarioDialogComponent } from './components/add-vita-beneficiario-dialog/add-vita-beneficiario-dialog.component';
import {
  VitaBeneficiarioDetailDialogComponent,
  VitaBeneficiarioDetailResult,
} from './components/vita-beneficiario-detail-dialog/vita-beneficiario-detail-dialog.component';
import {
  CotizacionCo,
  CotizacionClp,
  CotizacionVes,
  OthersPayoutsService,
  RemesaCoErrorAccionable,
  RemesaCoRail,
  RemesaCoResponse,
  RemesaClpResponse,
  RemesaVesResponse,
  ValoresCambiaron,
  esErrorAccionable,
} from '../../services/others-payouts.service';
import {
  CotizacionPe,
  CotizacionVesPe,
  CotizacionClpPe,
  PeruPayoutsService,
  RemesaPeResponse,
  RemesaVesPeResponse,
  RemesaClpPeResponse,
  ValoresCambiaronPe,
} from '../../services/peru-payouts.service';
import { WalletVesClientService } from '../../services/wallet-ves-client.service';
import { CartolaService } from '../../services/cartola.service';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

/**
 * Origen de fondos del envío: USDT (todos), CLP (clientes de Chile) o VES
 * (usuarios venezolanos pagan con su saldo VES; espejo del flujo CLP).
 */
type OrigenEnvio = 'USDT' | 'CLP' | 'VES';

/** Límites de la remesa USDT→COP (espejo del backend). */
const REMESA_MIN_USD = 20;
const REMESA_MAX_USD = 2500;

/**
 * Mapeo de keys del schema dinámico de Vita a las columnas fijas del
 * entity de beneficiarios. Se usa para hidratar `formValues` cuando el
 * usuario elige un beneficiario ya guardado (para renderizar el resumen).
 */
const IDENTITY_MAP: Record<string, string> = {
  beneficiary_first_name: 'nombre',
  beneficiary_last_name: 'apellidos',
  beneficiary_email: 'email',
  beneficiary_document_type: 'tipoId',
  beneficiary_document_number: 'numeroId',
  phone: 'telefono',
};

type Step = 'calc' | 'beneficiary' | 'summary' | 'done';

/** UUID v4 para la idempotencia del envío (WS1.1). Fallback para WebViews viejos. */
function nuevoRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function') {
    return (crypto as any).randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** Labels legibles de monedas destino (ISO 4217 → nombre local). */
const CURRENCY_NAMES: Record<string, string> = {
  USD: 'Dólares',
  EUR: 'Euros',
  GBP: 'Libras',
  CLP: 'Pesos Chilenos',
  COP: 'Pesos Colombianos',
  VES: 'Bolívares',
  ARS: 'Pesos Argentinos',
  MXN: 'Pesos Mexicanos',
  BRL: 'Reales',
  PEN: 'Soles',
  BOB: 'Bolivianos',
  PYG: 'Guaraníes',
  UYU: 'Pesos Uruguayos',
  DOP: 'Pesos Dominicanos',
  HTG: 'Gourdes',
  CNY: 'Yuanes',
  CRC: 'Colones',
  GTQ: 'Quetzales',
  AUD: 'Dólares Australianos',
  HKD: 'Dólares de Hong Kong',
};

// Vita expone por field solo: key, name, type, min, max, options, when.
// No hay pattern/regex propio — respetamos exactamente lo que Vita manda.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Banderas locales por ISO (`assets/flags/*.png`). OJO: el `flagIcon` que
 * manda el backend es un EMOJI (🇨🇴), no una URL — no sirve para `<img src>`
 * (en Windows/Chrome además se renderiza como letras). PNG local primero;
 * emoji como fallback para países sin asset.
 */
const FLAG_ASSETS: Record<string, string> = {
  AR: 'assets/flags/argentina.png',
  CL: 'assets/flags/chile.png',
  CO: 'assets/flags/colombia.png',
  PE: 'assets/flags/peru.png',
  VE: 'assets/flags/venezuela.png',
};

@Component({
  selector: 'app-vita-calculator-demo',
  templateUrl: './vita-calculator-demo.component.html',
  styleUrls: ['./vita-calculator-demo.component.sass'],
})
export class VitaCalculatorDemoComponent implements OnInit, OnDestroy {
  step: Step = 'calc';

  countries: CalculatorCountry[] = [];
  countriesLoading = true;
  countriesError: string | null = null;

  selectedCountryCode = '';
  variants: CalculatorVariant[] = [];
  variantsLoading = false;
  variantsError: string | null = null;
  selectedVariantCode = '';

  /** Inicia en el mínimo (20 USD); el usuario lo ajusta. */
  amountUsd: number | null = 20;

  /** Desglose de la cotización (sobre "Continuar"): arranca SIEMPRE cerrado. */
  desgloseAbierto = false;

  /** Desglose del flujo CLP→COP. Independiente del de USDT (bloques distintos). */
  desgloseClpAbierto = false;

  /** Desglose del flujo VES→COP / VES→PEN. Independiente de los otros dos. */
  desgloseVesAbierto = false;

  estimate: CalculatorEstimateResult | null = null;
  estimating = false;
  estimateError: string | null = null;

  schema: CalculatorFormSchema | null = null;
  schemaLoading = false;
  schemaError: string | null = null;

  /** Valores del formulario de beneficiario (key → value). */
  formValues: Record<string, any> = {};
  formErrors: Record<string, string> = {};

  // ─── Libreta de beneficiarios ───
  beneficiarios: VitaBeneficiario[] = [];
  beneficiariosLoading = false;
  beneficiariosError: string | null = null;
  selectedBeneficiarioId: string | null = null;
  searchTxt = '';

  // ─── Envío productivo (rail USDT→COP, solo Colombia) ───
  enviando = false;
  envioResult: RemesaCoResponse | null = null;
  envioError: string | null = null;

  /**
   * Idempotencia (WS1.1): UNA intención de envío = UN requestId. Se regenera
   * tras un envío EXITOSO y al confirmar un POSIBLE_DUPLICADO (si se reusara,
   * el replay del backend se tragaría el segundo envío intencional). Un retry
   * de red / re-confirmación tras VALORES_CAMBIARON reusa el MISMO id.
   */
  private requestId = nuevoRequestId();

  /**
   * Mensaje de bloqueo que deriva al usuario a un operador de Aguacate cuando
   * NO puede enviar: datos incompletos (del emisor o del beneficiario) o
   * beneficiario bloqueado. Reemplaza el flujo de autoservicio — el cliente no
   * completa nada por su cuenta, contacta a un operador (empresa o persona).
   */
  bloqueoOperador: string | null = null;

  /** Textos client-facing del bloqueo → operador (sin mención a "Vita"). */
  readonly MSG_OP_REMITENTE =
    'No podemos procesar tu envío porque faltan datos de tu perfil. Comunicate con un operador de Aguacate para completar tu información.';
  readonly MSG_OP_BENEFICIARIO =
    'No podemos procesar el envío porque faltan datos del beneficiario. Comunicate con un operador de Aguacate para completar la información.';
  readonly MSG_OP_BANNED =
    'Este beneficiario no está habilitado para recibir transferencias. Comunicate con un operador de Aguacate.';

  // Cotización real (Modelo A) para CO — lo que ve el cliente (concuerda con el envío).
  cotizacion: CotizacionCo | null = null;
  cotizando = false;

  // ─── Envío productivo USD→PEN (destino Perú; espejo de CO, moneda con 2 decimales) ───
  cotizacionPe: CotizacionPe | null = null;
  cotizandoPe = false;
  envioResultPe: RemesaPeResponse | null = null;

  /** Aviso: la tasa cambió entre el preview y el confirm; valores ya ajustados. */
  valoresCambiaron = false;

  private amount$ = new Subject<number | null>();
  private sub = new Subscription();

  /**
   * Inputs (rail|monto) para los que la conversión EN PANTALLA es vigente. Si
   * no coincide con los inputs actuales, hay un recálculo pendiente/en vuelo y
   * "Continuar" no debe habilitarse (el monto cambia al instante por ngModel,
   * antes del debounce, así que esto cierra esa ventana sin flags que se traben).
   */
  private cotizadoSnapshot: string | null = null;

  constructor(
    private calc: VitaCalculatorService,
    private beneficiarioSvc: VitaBeneficiarioService,
    private dialog: MatDialog,
    private othersPayouts: OthersPayoutsService,
    private peruPayouts: PeruPayoutsService,
    private walletVes: WalletVesClientService,
    private cartola: CartolaService,
    private store: Store<any>,
    private router: Router,
  ) {}

  /** Saldo USDT del cliente (para que sepa cuánto puede enviar). */
  saldoUsdt: number | null = null;
  readonly minUsd = REMESA_MIN_USD;
  readonly maxUsd = REMESA_MAX_USD;

  // ─── Origen de fondos (USDT / CLP) ───
  /** tipo de usuario: 'VE' = venezolano (solo USDT); resto = Chile (USDT o CLP). */
  tipoId = '';
  private usuarioId = '';
  /** Origen por defecto SIEMPRE USDT; CL puede cambiarlo a CLP. */
  origen: OrigenEnvio = 'USDT';
  /** Saldo CLP disponible (incluye sobregiro) — solo clientes CL. */
  saldoClp: number | null = null;

  // ─── Calculadora CLP→COP / CLP→PEN (origen CLP; Colombia o Perú) ───
  /** Destino elegido por el usuario (no auto). '' = todavía no eligió país. */
  clpDestino: '' | 'CO' | 'PE' = '';
  /** Inicia en ~20 USD equivalente (CLP); el usuario lo ajusta. */
  montoClp: number | null = 20000;
  railClp: RemesaCoRail = 'COBREB';
  cotizacionClp: CotizacionClp | null = null;
  /** Cotización CLP→PEN (destino Perú, moneda PEN). Paralela a `cotizacionClp` (COP). */
  cotizacionClpPe: CotizacionClpPe | null = null;
  cotizandoClp = false;
  private montoClp$ = new Subject<number | null>();
  /** Resultado del envío CLP→COP (paso 'done'). Paralelo a `envioResult` (USDT). */
  envioResultClp: RemesaClpResponse | null = null;
  /** Resultado del envío CLP→PEN (paso 'done'). */
  envioResultClpPe: RemesaClpPeResponse | null = null;

  // ─── Calculadora VES→COP (origen VES; usuarios VE; espejo de CLP) ───
  /** Saldo VES disponible (sin sobregiro) — solo usuarios VE. */
  saldoVes: number | null = null;
  /** Destino elegido por el usuario (no auto). '' = todavía no eligió país. */
  vesDestino: '' | 'CO' | 'PE' = '';
  /** Inicia en ~20 USD equivalente (VES); el usuario lo ajusta. */
  montoVes: number | null = 20000;
  railVes: RemesaCoRail = 'COBREB';
  cotizacionVes: CotizacionVes | null = null;
  /** Cotización VES→PEN (destino Perú, moneda PEN). Paralela a `cotizacionVes` (COP). */
  cotizacionVesPe: CotizacionVesPe | null = null;
  envioResultVesPe: RemesaVesPeResponse | null = null;
  cotizandoVes = false;
  private montoVes$ = new Subject<number | null>();
  /** Resultado del envío VES→COP (paso 'done'). Paralelo a `envioResult` (USDT). */
  envioResultVes: RemesaVesResponse | null = null;

  ngOnInit(): void {
    this.loadCountries();
    this.cargarSaldoUsdt();
    // Identidad del usuario: tipoId (VE/CL) gobierna los orígenes disponibles;
    // usuarioId resuelve el saldo CLP. Best-effort (si no llega, queda USDT).
    this.sub.add(
      this.store.select('auth').subscribe(({ user }: any) => {
        this.tipoId = user?.tipoId || '';
        this.usuarioId = user?.usuarioId != null ? String(user.usuarioId) : '';
        // Origen arranca en USDT (default). Para CL precargo el saldo CLP por si
        // cambia el origen; VE solo opera USDT.
        if (this.tipoId && this.tipoId !== 'VE' && this.saldoClp === null) {
          this.cargarSaldoClp();
        }
        // Usuarios VE: precargo el saldo VES por si elige el origen VES.
        if (this.tipoId === 'VE' && this.saldoVes === null) {
          this.cargarSaldoVes();
        }
      }),
    );
    // Calc CLP→COP con debounce (origen CLP).
    this.sub.add(
      this.montoClp$
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe(() => this.cotizarClpCalc()),
    );
    // Calc VES→COP con debounce (origen VES).
    this.sub.add(
      this.montoVes$
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe(() => this.cotizarVesCalc()),
    );
    // Recalcular con debounce cuando cambia el monto
    this.sub.add(
      this.amount$
        .pipe(debounceTime(350), distinctUntilChanged())
        .subscribe(() => this.runEstimate()),
    );
    // Auto-refresh cada 2 min para ver cambios de tasas Vita
    this.sub.add(
      interval(120_000).subscribe(() => {
        if (this.step === 'calc' && this.selectedVariantCode) {
          this.loadVariants(this.selectedCountryCode, false);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  // -------------------- Paso 1: Cálculo --------------------

  loadCountries(): void {
    this.countriesLoading = true;
    this.countriesError = null;
    this.sub.add(
      this.calc.listCountries().subscribe({
        next: (list) => {
          this.countries = list;
          this.countriesLoading = false;
        },
        error: (err) => {
          this.countriesError =
            typeof err === 'string' ? err : 'Error obteniendo países';
          this.countriesLoading = false;
        },
      }),
    );
  }

  onCountryChange(): void {
    this.variants = [];
    this.selectedVariantCode = '';
    this.estimate = null;
    this.estimateError = null;
    if (!this.selectedCountryCode) return;
    this.loadVariants(this.selectedCountryCode, true);
  }

  loadVariants(countryCode: string, autoSelectFirst: boolean): void {
    this.variantsLoading = true;
    this.variantsError = null;
    this.sub.add(
      this.calc.getVariants(countryCode).subscribe({
        next: (list) => {
          // Perú: este rail SOLO entrega PEN (variante `pe`). Vita también publica
          // `peusd` (entrega USD en Perú), que NO soportamos → la excluimos para
          // no enviar algo distinto de lo que el cliente ve (evita el mismatch
          // "Perú · USD" mostrando/cobrando PEN).
          const variantes =
            (countryCode || '').toUpperCase() === 'PE'
              ? list.filter((v) => !/usd/i.test(v.code || ''))
              : list;
          this.variants = variantes;
          this.variantsLoading = false;
          if (autoSelectFirst && variantes.length > 0) {
            this.selectedVariantCode = variantes[0].code;
          }
          if (this.selectedVariantCode) {
            this.runEstimate();
          }
        },
        error: (err) => {
          this.variantsError =
            typeof err === 'string' ? err : 'Error obteniendo variantes';
          this.variantsLoading = false;
        },
      }),
    );
  }

  onVariantChange(): void {
    this.estimate = null;
    this.estimateError = null;
    if (this.selectedVariantCode) this.runEstimate();
  }

  onAmountChange(): void {
    this.amount$.next(this.amountUsd);
  }

  /** Carga el saldo USDT del cliente (best-effort). */
  private cargarSaldoUsdt(): void {
    this.sub.add(
      this.walletVes.getMyUsdtBalance().subscribe({
        next: (res) => {
          this.saldoUsdt = Number(res?.data?.saldo ?? 0);
        },
        error: () => {
          this.saldoUsdt = null;
        },
      }),
    );
  }

  // ─── Origen de fondos ───

  /** VE = VES o USDT; resto (Chile) = USDT o CLP. */
  get esVe(): boolean {
    return this.tipoId === 'VE';
  }
  get origenes(): OrigenEnvio[] {
    // VE: VES primero (su moneda local) + USDT. La disponibilidad del ENVÍO la
    // gatea la cotización (`activo`), igual que CLP — el origen se muestra igual.
    return this.esVe ? ['VES', 'USDT'] : ['USDT', 'CLP'];
  }
  get esOrigenClp(): boolean {
    return this.origen === 'CLP';
  }
  get esOrigenVes(): boolean {
    return this.origen === 'VES';
  }

  selectOrigen(o: OrigenEnvio): void {
    if (this.origen === o) return;
    this.origen = o;
    this.clpDestino = ''; // el país lo elige el usuario, no se auto-selecciona
    this.vesDestino = '';
    this.cotizacionClp = null;
    this.cotizacionVes = null;
    // Limpiar el estado del flujo USDT para que no quede país/variante/cotización
    // viejos al alternar de origen (goToBeneficiaryClp fija selectedCountryCode='CO').
    this.selectedCountryCode = '';
    this.selectedVariantCode = '';
    this.variants = [];
    this.estimate = null;
    this.cotizacion = null;
    this.cotizadoSnapshot = null;
    if (o === 'CLP' && this.saldoClp === null) this.cargarSaldoClp();
    if (o === 'VES' && this.saldoVes === null) this.cargarSaldoVes();
  }

  /** El usuario elige el país destino del flujo CLP (Colombia o Perú). */
  selectClpPais(c: 'CO' | 'PE'): void {
    this.clpDestino = c;
    this.cotizacionClp = null;
    this.cotizacionClpPe = null;
    this.cotizarClpCalc();
  }

  /** El envío CLP es a Perú (destino PEN). Paralelo a CLP→COP. */
  get esClpPeFlow(): boolean {
    return this.origen === 'CLP' && this.clpDestino === 'PE';
  }

  /** Saldo CLP disponible (balancePermitido = saldo + cupo sobregiro). */
  private cargarSaldoClp(): void {
    if (!this.usuarioId) return;
    this.cartola
      .getSaldoCliente(this.usuarioId)
      .then((r) => {
        this.saldoClp = Number(r?.balancePermitido ?? r?.saldo ?? 0);
      })
      .catch(() => {
        this.saldoClp = null;
      });
  }

  /** Saldo VES disponible (último balance del cardex VES, sin sobregiro). */
  private cargarSaldoVes(): void {
    this.sub.add(
      this.walletVes.getMyBalance().subscribe({
        next: (res) => {
          this.saldoVes = Number(res?.data?.balanceVes ?? 0);
        },
        error: () => {
          this.saldoVes = null;
        },
      }),
    );
  }

  // ─── Calculadora CLP→COP (origen CLP) ───

  onMontoClpChange(): void {
    this.montoClp$.next(this.montoClp);
  }

  setRailClp(rail: RemesaCoRail): void {
    if (this.railClp === rail) return;
    this.railClp = rail;
    this.cotizarClpCalc();
  }

  // ─── Desglose CLP→COP: tasa de REFERENCIA + comisión (estilo cotizador
  //     público). Espejo de lo que hace el bloque USDT, pero en moneda de
  //     origen. NO recalcula el envío: `recibeCop` es el del backend. ───

  /**
   * Tasa BRUTA COP por CLP, antes de comisión — la que manda el backend en
   * `tasaReferenciaCopClp`. `null` cuando no hay referencia confiable (feed
   * caído, o nuestra tasa CLP es mejor que la pública): ahí el bloque cae a
   * mostrar sólo la efectiva, como hasta ahora.
   */
  get tasaReferenciaClp(): number | null {
    if (this.clpDestino === 'PE') {
      return this.cotizacionClpPe?.disponible === true
        ? this.cotizacionClpPe?.tasaReferenciaPenClp ?? null
        : null;
    }
    return this.cotizacionClp?.disponible === true
      ? this.cotizacionClp?.tasaReferenciaCopClp ?? null
      : null;
  }

  /** Moneda destino del flujo CLP (COP o PEN). */
  get monedaDestinoClp(): string {
    return this.clpDestino === 'PE' ? 'PEN' : 'COP';
  }

  /** Lo que recibe el destinatario en el flujo CLP, en su moneda. */
  get recibeDestinoClp(): number | null {
    return this.clpDestino === 'PE'
      ? this.cotizacionClpPe?.recibePen ?? null
      : this.cotizacionClp?.recibeCop ?? null;
  }

  /** Formato del recibe: COP entero, PEN dos decimales. */
  get fmtRecibeClp(): string {
    return this.clpDestino === 'PE' ? '1.2-2' : '1.0-0';
  }

  /**
   * Formato de la tasa de referencia. COP por CLP vive en unidades (3,39 → dos
   * decimales alcanzan); PEN por CLP vive en milésimas (0,003647) y con dos
   * decimales se mostraría "0.00", así que ahí van 4-6 — el mismo formato que ya
   * usa la línea de tasa efectiva de ese par.
   */
  get fmtRefClp(): string {
    return this.clpDestino === 'PE' ? '1.4-6' : '1.2-2';
  }

  /**
   * Comisión en CLP = monto − recibe ÷ tasaReferencia. Es el residuo que hace
   * cerrar `(monto − comisión) × referencia = recibe`, así que el desglose
   * cuadra exactamente con lo que informa el backend.
   */
  get comisionClp(): number | null {
    const ref = this.tasaReferenciaClp;
    const recibe = this.recibeDestinoClp;
    if (!ref || !recibe || !this.montoClp) return null;
    return Math.max(0, this.montoClp - recibe / ref);
  }

  /** Sólo se puede desplegar con cotización vigente y referencia disponible. */
  get puedeDesplegarDesgloseClp(): boolean {
    const disponible =
      this.clpDestino === 'PE'
        ? this.cotizacionClpPe?.disponible === true
        : this.cotizacionClp?.disponible === true;
    return (
      !!this.montoClp &&
      !this.cotizandoClp &&
      disponible &&
      this.tasaReferenciaClp != null &&
      this.comisionClp != null
    );
  }

  /** Plazo legible del flujo CLP según destino. */
  get plazoDestinoClp(): string {
    return this.clpDestino === 'PE' ? 'menos de 1 hora' : this.plazoClpLabel;
  }

  /** Plazo legible del riel CLP elegido. */
  get plazoClpLabel(): string {
    const d = this.cotizacionClp?.businessDays;
    const dd = d != null ? d : this.railClp === 'COBREB' ? 0 : 1;
    if (dd <= 0) return 'menos de 1 hora';
    return dd === 1 ? '1 día hábil' : `${dd} días hábiles`;
  }

  /** Cotiza CLP→COP o CLP→PEN (client-safe) para el monto/destino actuales. */
  cotizarClpCalc(): void {
    if (this.origen !== 'CLP' || !this.clpDestino) return;
    if (!this.montoClp || this.montoClp <= 0) {
      this.cotizacionClp = null;
      this.cotizacionClpPe = null;
      return;
    }
    this.cotizandoClp = true;
    // CLP→PEN: convierte CLP→USD y corre el motor USD→PEN (recibe PEN 2 dec.).
    if (this.clpDestino === 'PE') {
      this.cotizacionClpPe = null;
      this.sub.add(
        this.peruPayouts.cotizarClp(Number(this.montoClp)).subscribe({
          next: (c) => {
            this.cotizacionClpPe = c;
            this.cotizandoClp = false;
          },
          error: () => {
            this.cotizacionClpPe = { disponible: false };
            this.cotizandoClp = false;
          },
        }),
      );
      return;
    }
    this.cotizacionClp = null; // limpia el valor viejo mientras calcula (sin "tasa prefijada")
    this.sub.add(
      this.othersPayouts.cotizarClp(this.railClp, Number(this.montoClp)).subscribe({
        next: (c) => {
          this.cotizacionClp = c;
          this.cotizandoClp = false;
        },
        error: () => {
          this.cotizacionClp = { disponible: false };
          this.cotizandoClp = false;
        },
      }),
    );
  }

  // ─── Flujo de envío CLP→COP (origen CLP) ───

  /** El envío en curso paga con CLP (no USDT). Gobierna display + dispatch. */
  get esClpFlow(): boolean {
    return this.origen === 'CLP';
  }

  /** El monto CLP supera el saldo disponible (incluye sobregiro). Bloquea envío. */
  get saldoClpInsuficiente(): boolean {
    return (
      this.saldoClp != null &&
      this.montoClp != null &&
      this.montoClp > this.saldoClp
    );
  }

  /** Mínimo en CLP del riel elegido (COP o PEN), a la tasa vigente. */
  get minClpVigente(): number | null {
    const c: any = this.esClpPeFlow ? this.cotizacionClpPe : this.cotizacionClp;
    return c?.minClp ?? null;
  }

  /**
   * Falta saldo CLP. Cubre los DOS casos que se resuelven con una recarga:
   *
   *  1. el monto tipeado supera el saldo;
   *  2. el saldo no llega ni al mínimo del riel (20 USD).
   *
   * El segundo hacía falta: con poco saldo el cliente tipea montos chicos, la
   * cotización volvía `bajoMinimo` y el aviso de saldo —condicionado a
   * `!bajoMinimo`— no aparecía nunca. Veía "el mínimo es X" sin enterarse de
   * que tampoco tenía con qué pagarlo.
   */
  get faltaSaldoClp(): boolean {
    if (this.saldoClp == null) return false;
    if (this.saldoClpInsuficiente) return true;
    const min = this.minClpVigente;
    return min != null && this.saldoClp < min;
  }

  /**
   * Habilita "Continuar" del flujo CLP: cotización viva y disponible, no bajo
   * mínimo, rail activo, saldo suficiente y nada en vuelo.
   */
  get puedeContinuarClp(): boolean {
    if (this.origen !== 'CLP' || !this.clpDestino) return false;
    if (this.cotizandoClp) return false;
    if (this.saldoClpInsuficiente) return false;
    const c: any = this.esClpPeFlow ? this.cotizacionClpPe : this.cotizacionClp;
    if (!c || c.disponible !== true) return false;
    if (c.bajoMinimo || c.excedeMaximo) return false;
    if (c.activo === false) return false;
    return true;
  }

  /**
   * Avanza al beneficiario reusando el paso del flujo CO/PE: fija país=destino y
   * variante=rail elegido, carga schema + libreta. El resto del wizard
   * (beneficiario → resumen) es común; solo el envío final difiere.
   */
  goToBeneficiaryClp(): void {
    if (!this.puedeContinuarClp) return;
    this.selectedCountryCode = this.clpDestino; // 'CO' | 'PE'
    this.selectedVariantCode = this.esClpPeFlow ? 'pe' : this.railClp;
    this.loadSchema(this.selectedVariantCode);
    this.loadBeneficiarios();
    this.step = 'beneficiary';
  }

  /** Dispatcher del botón "Confirmar y enviar" del resumen (VES vs CLP vs USDT). */
  confirmarRemesaActual(): void {
    if (this.esVesPeFlow) this.confirmarEnvioVesPe();
    else if (this.esClpPeFlow) this.confirmarEnvioClpPe();
    else if (this.esVesFlow) this.confirmarEnvioVes();
    else if (this.esClpFlow) this.confirmarEnvioClp();
    else if (this.esPeru) this.confirmarEnvioPe();
    else this.confirmarEnvio();
  }

  /**
   * Crea la remesa real CLP→COP (`POST /colombia/clp/remesa`). Espejo de
   * `confirmarEnvio` pero paga con CLP. Mismo manejo de errores accionables.
   */
  confirmarEnvioClp(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResultClp) return;
    if (
      this.origen !== 'CLP' ||
      !this.selectedBeneficiarioId ||
      this.cotizacionClp?.disponible !== true ||
      !this.montoClp
    ) {
      return;
    }
    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.othersPayouts
        .enviarRemesaClp({
          rail: this.railClp,
          montoClp: this.montoClp,
          beneficiarioId: this.selectedBeneficiarioId,
          expectedRecibeCop: this.cotizacionClp?.recibeCop,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResultClp = res;
            this.enviando = false;
            this.requestId = nuevoRequestId();
            this.cargarSaldoClp();
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            if (err && (err as ValoresCambiaron).cambioValores) {
              const v = err as ValoresCambiaron;
              this.cotizacionClp = {
                disponible: true,
                rail: this.railClp,
                montoClp: this.montoClp!,
                recibeCop: v.recibeCop,
                tasaCopClp: v.tasaCop,
                businessDays: v.businessDays,
                activo: true,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  /**
   * Crea la remesa real CLP→PEN (`POST /peru/clp/remesa`). Espejo de
   * `confirmarEnvioClp` (CO) para destino Perú, moneda PEN. Reversa CLP vía SQS.
   */
  confirmarEnvioClpPe(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResultClpPe) return;
    if (
      !this.esClpPeFlow ||
      !this.selectedBeneficiarioId ||
      this.cotizacionClpPe?.disponible !== true ||
      !this.montoClp
    ) {
      return;
    }
    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.peruPayouts
        .enviarRemesaClp({
          montoClp: this.montoClp,
          beneficiarioId: this.selectedBeneficiarioId,
          expectedRecibePen: this.cotizacionClpPe?.recibePen,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResultClpPe = res;
            this.enviando = false;
            this.requestId = nuevoRequestId();
            this.cargarSaldoClp();
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            if (err && (err as ValoresCambiaronPe).cambioValores) {
              const v = err as ValoresCambiaronPe;
              this.cotizacionClpPe = {
                disponible: true,
                rail: 'PE',
                montoClp: this.montoClp!,
                recibePen: v.recibePen,
                tasaPenClp: v.tasaPen,
                businessDays: v.businessDays,
                activo: true,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err as any);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  // ─── Calculadora VES→COP (origen VES; espejo de CLP) ───

  onMontoVesChange(): void {
    this.montoVes$.next(this.montoVes);
  }

  setRailVes(rail: RemesaCoRail): void {
    if (this.railVes === rail) return;
    this.railVes = rail;
    this.cotizarVesCalc();
  }

  /** El usuario elige el país destino del flujo VES (Colombia o Perú). */
  selectVesPais(c: 'CO' | 'PE'): void {
    this.vesDestino = c;
    this.cotizacionVes = null;
    this.cotizacionVesPe = null;
    this.cotizarVesCalc();
  }

  /** El envío VES es a Perú (destino PEN). Paralelo a VES→COP. */
  get esVesPeFlow(): boolean {
    return this.origen === 'VES' && this.vesDestino === 'PE';
  }

  /** Plazo legible del riel VES elegido. */
  get plazoVesLabel(): string {
    const d = this.cotizacionVes?.businessDays;
    const dd = d != null ? d : this.railVes === 'COBREB' ? 0 : 1;
    if (dd <= 0) return 'menos de 1 hora';
    return dd === 1 ? '1 día hábil' : `${dd} días hábiles`;
  }

  // ─── Desglose VES→COP / VES→PEN: tasa de REFERENCIA + comisión. Espejo del
  //     bloque CLP. La referencia la compone el backend contra NUESTRA tasa base
  //     (el partner no cotiza VES), así que siempre supera a la efectiva. ───

  /** Tasa BRUTA de la moneda destino por VES, antes de comisión. */
  get tasaReferenciaVes(): number | null {
    if (this.vesDestino === 'PE') {
      return this.cotizacionVesPe?.disponible === true
        ? this.cotizacionVesPe?.tasaReferenciaPenVes ?? null
        : null;
    }
    return this.cotizacionVes?.disponible === true
      ? this.cotizacionVes?.tasaReferenciaCopVes ?? null
      : null;
  }

  /** Moneda destino del flujo VES (COP o PEN). */
  get monedaDestinoVes(): string {
    return this.vesDestino === 'PE' ? 'PEN' : 'COP';
  }

  /** Lo que recibe el destinatario en el flujo VES, en su moneda. */
  get recibeDestinoVes(): number | null {
    return this.vesDestino === 'PE'
      ? this.cotizacionVesPe?.recibePen ?? null
      : this.cotizacionVes?.recibeCop ?? null;
  }

  /** Formato del recibe: COP entero, PEN dos decimales. */
  get fmtRecibeVes(): string {
    return this.vesDestino === 'PE' ? '1.2-2' : '1.0-0';
  }

  /**
   * Formato de la tasa de referencia por VES. Las dos viven en unidades chicas
   * (COP/VES ≈ 4, PEN/VES ≈ 0,004), así que PEN necesita 4-6 decimales o se
   * mostraría "0.00" — igual que en CLP→PEN.
   */
  get fmtRefVes(): string {
    return this.vesDestino === 'PE' ? '1.4-6' : '1.2-4';
  }

  /** Comisión en VES = monto − recibe ÷ tasaReferencia (residuo, cierra la identidad). */
  get comisionVes(): number | null {
    const ref = this.tasaReferenciaVes;
    const recibe = this.recibeDestinoVes;
    if (!ref || !recibe || !this.montoVes) return null;
    return Math.max(0, this.montoVes - recibe / ref);
  }

  /** Sólo se puede desplegar con cotización vigente y referencia disponible. */
  get puedeDesplegarDesgloseVes(): boolean {
    const disponible =
      this.vesDestino === 'PE'
        ? this.cotizacionVesPe?.disponible === true
        : this.cotizacionVes?.disponible === true;
    return (
      !!this.montoVes &&
      !this.cotizandoVes &&
      disponible &&
      this.tasaReferenciaVes != null &&
      this.comisionVes != null
    );
  }

  /** Plazo legible del flujo VES según destino. */
  get plazoDestinoVes(): string {
    return this.vesDestino === 'PE' ? 'menos de 1 hora' : this.plazoVesLabel;
  }

  /** Cotiza VES→COP o VES→PEN (client-safe) para el monto/destino actuales. */
  cotizarVesCalc(): void {
    if (this.origen !== 'VES' || !this.vesDestino) return;
    if (!this.montoVes || this.montoVes <= 0) {
      this.cotizacionVes = null;
      this.cotizacionVesPe = null;
      return;
    }
    this.cotizandoVes = true;
    // VES→PEN: convierte VES→USD y corre el motor USD→PEN (recibe PEN 2 dec.).
    if (this.vesDestino === 'PE') {
      this.cotizacionVesPe = null;
      this.sub.add(
        this.peruPayouts.cotizarVes(Number(this.montoVes)).subscribe({
          next: (c) => {
            this.cotizacionVesPe = c;
            this.cotizandoVes = false;
          },
          error: () => {
            this.cotizacionVesPe = { disponible: false };
            this.cotizandoVes = false;
          },
        }),
      );
      return;
    }
    this.cotizacionVes = null; // limpia el valor viejo mientras calcula
    this.sub.add(
      this.othersPayouts.cotizarVes(this.railVes, Number(this.montoVes)).subscribe({
        next: (c) => {
          this.cotizacionVes = c;
          this.cotizandoVes = false;
        },
        error: () => {
          this.cotizacionVes = { disponible: false };
          this.cotizandoVes = false;
        },
      }),
    );
  }

  // ─── Flujo de envío VES→COP (origen VES) ───

  /** El envío en curso paga con VES (no USDT/CLP). Gobierna display + dispatch. */
  get esVesFlow(): boolean {
    return this.origen === 'VES';
  }

  /** El monto VES supera el saldo disponible (sin sobregiro). Bloquea envío. */
  get saldoVesInsuficiente(): boolean {
    return (
      this.saldoVes != null &&
      this.montoVes != null &&
      this.montoVes > this.saldoVes
    );
  }

  /** Mínimo en VES del riel elegido (COP o PEN), a la tasa vigente. */
  get minVesVigente(): number | null {
    const c: any = this.esVesPeFlow ? this.cotizacionVesPe : this.cotizacionVes;
    return c?.minVes ?? null;
  }

  /** Falta saldo VES. Mismos dos casos que `faltaSaldoClp`. */
  get faltaSaldoVes(): boolean {
    if (this.saldoVes == null) return false;
    if (this.saldoVesInsuficiente) return true;
    const min = this.minVesVigente;
    return min != null && this.saldoVes < min;
  }

  /**
   * Habilita "Continuar" del flujo VES: cotización viva y disponible, no bajo
   * mínimo ni sobre máximo, rail activo, saldo suficiente y nada en vuelo.
   */
  get puedeContinuarVes(): boolean {
    if (this.origen !== 'VES' || !this.vesDestino) return false;
    if (this.cotizandoVes) return false;
    if (this.saldoVesInsuficiente) return false;
    const c: any = this.esVesPeFlow ? this.cotizacionVesPe : this.cotizacionVes;
    if (!c || c.disponible !== true) return false;
    if (c.bajoMinimo || c.excedeMaximo) return false;
    if (c.activo === false) return false;
    return true;
  }

  /**
   * Avanza al beneficiario reusando el paso del flujo CO: fija país=CO y
   * variante=rail elegido, carga schema + libreta. El resto del wizard
   * (beneficiario → resumen) es común; solo el envío final difiere.
   */
  goToBeneficiaryVes(): void {
    if (!this.puedeContinuarVes) return;
    this.selectedCountryCode = this.vesDestino; // 'CO' | 'PE'
    this.selectedVariantCode = this.esVesPeFlow ? 'pe' : this.railVes;
    this.loadSchema(this.selectedVariantCode);
    this.loadBeneficiarios();
    this.step = 'beneficiary';
  }

  /**
   * Crea la remesa real VES→COP (`POST /colombia/ves/remesa`). Espejo de
   * `confirmarEnvioClp` pero paga con VES. Mismo manejo de errores accionables.
   */
  confirmarEnvioVes(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResultVes) return;
    if (
      this.origen !== 'VES' ||
      !this.selectedBeneficiarioId ||
      this.cotizacionVes?.disponible !== true ||
      !this.montoVes
    ) {
      return;
    }
    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.othersPayouts
        .enviarRemesaVes({
          rail: this.railVes,
          montoVes: this.montoVes,
          beneficiarioId: this.selectedBeneficiarioId,
          expectedRecibeCop: this.cotizacionVes?.recibeCop,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResultVes = res;
            this.enviando = false;
            this.requestId = nuevoRequestId();
            this.cargarSaldoVes();
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            if (err && (err as ValoresCambiaron).cambioValores) {
              const v = err as ValoresCambiaron;
              this.cotizacionVes = {
                disponible: true,
                rail: this.railVes,
                montoVes: this.montoVes!,
                recibeCop: v.recibeCop,
                tasaCopVes: v.tasaCop,
                businessDays: v.businessDays,
                activo: true,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  /**
   * Crea la remesa real VES→PEN (`POST /peru/ves/remesa`). Espejo de
   * `confirmarEnvioVes` (CO) para destino Perú, moneda PEN. Reversa VES in-app.
   */
  confirmarEnvioVesPe(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResultVesPe) return;
    if (
      !this.esVesPeFlow ||
      !this.selectedBeneficiarioId ||
      this.cotizacionVesPe?.disponible !== true ||
      !this.montoVes
    ) {
      return;
    }
    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.peruPayouts
        .enviarRemesaVes({
          montoVes: this.montoVes,
          beneficiarioId: this.selectedBeneficiarioId,
          expectedRecibePen: this.cotizacionVesPe?.recibePen,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResultVesPe = res;
            this.enviando = false;
            this.requestId = nuevoRequestId();
            this.cargarSaldoVes();
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            if (err && (err as ValoresCambiaronPe).cambioValores) {
              const v = err as ValoresCambiaronPe;
              this.cotizacionVesPe = {
                disponible: true,
                rail: 'PE',
                montoVes: this.montoVes!,
                recibePen: v.recibePen,
                tasaPenVes: v.tasaPen,
                businessDays: v.businessDays,
                activo: true,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err as any);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  runEstimate(): void {
    if (!this.selectedCountryCode || !this.selectedVariantCode) {
      this.estimating = false;
      return;
    }
    if (!this.amountUsd || this.amountUsd <= 0) {
      this.estimate = null;
      this.estimateError = null;
      this.estimating = false;
      return;
    }
    // NI el mínimo (20 USD) NI el máximo NI el saldo cortan la cotización: el
    // cliente VE cuánto recibiría aunque no pueda enviar. El avance al envío se
    // bloquea aparte (`montoBajoMinimo`/`montoExcedeMaximo`/`saldoInsuficiente`).
    this.estimateError = null;
    // Limpiar el valor viejo YA: nada de "tasa prefijada" mientras calcula.
    this.estimate = null;
    this.cotizacion = null;
    this.cotizacionPe = null;
    this.cotizadoSnapshot = null;

    // Colombia: el valor real lo da la cotización (Modelo A), que NO tiene
    // mínimo/máximo — el cliente VE cuánto recibe a cualquier monto y el envío
    // se bloquea aparte. Saltamos el estimate de Vita (que sí tiene límites y
    // cortaba el cálculo por debajo del mínimo / por encima del máximo).
    if (this.esColombia) {
      this.estimating = false;
      this.fetchCotizacion();
      return;
    }

    // Perú: igual que CO — la cotización Modelo A (en PEN) es la fuente de verdad.
    if (this.esPeru) {
      this.estimating = false;
      this.fetchCotizacionPe();
      return;
    }

    // Otros países: estimate de Vita (con sus propios mínimos/máximos).
    this.estimating = true;
    const reqSnap = this.snapshotInputs;
    this.sub.add(
      this.calc
        .estimate({
          countryCode: this.selectedCountryCode,
          variant: this.selectedVariantCode,
          amountUsd: this.amountUsd!,
        })
        .subscribe({
          next: (res) => {
            this.estimate = res;
            this.estimating = false;
            this.cotizadoSnapshot = reqSnap;
          },
          error: (err) => {
            this.estimate = null;
            this.estimateError =
              typeof err === 'string' ? err : 'Error calculando el envío';
            this.estimating = false;
          },
        }),
    );
  }

  /** Cotización real del rail CO (oculta internos; concuerda con el envío). */
  private fetchCotizacion(): void {
    this.cotizando = true;
    this.cotizacion = null;
    this.valoresCambiaron = false;
    const reqSnap = this.snapshotInputs;
    this.sub.add(
      this.othersPayouts.cotizar(this.remesaRail, this.amountUsd!).subscribe({
        next: (c) => {
          this.cotizacion = c;
          this.cotizando = false;
          this.cotizadoSnapshot = reqSnap;
        },
        error: () => {
          this.cotizacion = { disponible: false };
          this.cotizando = false;
          this.cotizadoSnapshot = reqSnap;
        },
      }),
    );
  }

  /** Cotización real del rail Perú (USD→PEN, 2 decimales). Espejo de fetchCotizacion. */
  private fetchCotizacionPe(): void {
    this.cotizandoPe = true;
    this.cotizacionPe = null;
    this.valoresCambiaron = false;
    const reqSnap = this.snapshotInputs;
    this.sub.add(
      this.peruPayouts.cotizar(this.amountUsd!).subscribe({
        next: (c) => {
          this.cotizacionPe = c;
          this.cotizandoPe = false;
          this.cotizadoSnapshot = reqSnap;
        },
        error: () => {
          this.cotizacionPe = { disponible: false };
          this.cotizandoPe = false;
          this.cotizadoSnapshot = reqSnap;
        },
      }),
    );
  }

  // ─── Display cliente (limpio, sin internos). CO usa la cotización real;
  //     otros países usan el estimate genérico (sin mostrar ganancias). ───

  get displayDisponible(): boolean {
    // CO: solo "disponible" cuando la cotización ya cargó y es positiva (mientras
    // calcula, cotizacion es null → se muestra "calculando…", no un 0 viejo).
    if (this.esColombia) return this.cotizacion?.disponible === true;
    if (this.esPeru) return this.cotizacionPe?.disponible === true;
    return !!this.estimate;
  }

  get displayRecibe(): number {
    if (this.esVesPeFlow && this.cotizacionVesPe?.recibePen != null) {
      return this.cotizacionVesPe.recibePen;
    }
    if (this.esClpPeFlow && this.cotizacionClpPe?.recibePen != null) {
      return this.cotizacionClpPe.recibePen;
    }
    if (this.esVesFlow && this.cotizacionVes?.recibeCop != null) {
      return this.cotizacionVes.recibeCop;
    }
    if (this.esClpFlow && this.cotizacionClp?.recibeCop != null) {
      return this.cotizacionClp.recibeCop;
    }
    if (this.esColombia && this.cotizacion?.recibeCop != null) {
      return this.cotizacion.recibeCop;
    }
    if (this.esPeru && this.cotizacionPe?.recibePen != null) {
      return this.cotizacionPe.recibePen;
    }
    return this.estimate?.received || 0;
  }

  /**
   * Tasa EFECTIVA que ve el cliente = lo que recibe ÷ lo que envía. Así
   * `monto × tasa = recibe` concuerda sin mostrar fee/comisión ni internos.
   * CLP: COP/CLP (recibe ÷ montoClp); USDT: COP/USD (recibe ÷ montoUsd).
   */
  get displayTasa(): number {
    if (this.esVesFlow) {
      return this.montoVes && this.montoVes > 0 ? this.displayRecibe / this.montoVes : 0;
    }
    if (this.esClpFlow) {
      return this.montoClp && this.montoClp > 0 ? this.displayRecibe / this.montoClp : 0;
    }
    return this.amountUsd && this.amountUsd > 0 ? this.displayRecibe / this.amountUsd : 0;
  }

  get displayMoneda(): string {
    if (this.esPeru || this.esVesPeFlow || this.esClpPeFlow) return 'PEN';
    return (
      this.estimate?.destinationCurrency ||
      (this.esColombia || this.esClpFlow || this.esVesFlow ? 'COP' : '')
    );
  }

  get displayDias(): number {
    if (this.esVesPeFlow && this.cotizacionVesPe?.businessDays != null) {
      return this.cotizacionVesPe.businessDays;
    }
    if (this.esClpPeFlow && this.cotizacionClpPe?.businessDays != null) {
      return this.cotizacionClpPe.businessDays;
    }
    if (this.esVesFlow && this.cotizacionVes?.businessDays != null) {
      return this.cotizacionVes.businessDays;
    }
    if (this.esClpFlow && this.cotizacionClp?.businessDays != null) {
      return this.cotizacionClp.businessDays;
    }
    if (this.esColombia && this.cotizacion?.businessDays != null) {
      return this.cotizacion.businessDays;
    }
    if (this.esPeru && this.cotizacionPe?.businessDays != null) {
      return this.cotizacionPe.businessDays;
    }
    return this.estimate?.businessDays ?? 0;
  }

  /** Plazo legible para el cliente. 0 días NO es "instantáneo": acreditación < 1 hora. */
  get displayPlazo(): string {
    const d = this.displayDias;
    if (d <= 0) return 'menos de 1 hora';
    return d === 1 ? '1 día hábil' : `${d} días hábiles`;
  }

  /** Hay un recálculo en vuelo en el flujo USDT (estimate genérico, CO o PE). */
  get calculandoUsdt(): boolean {
    return (
      (this.estimating && !this.estimate) ||
      (this.esColombia && this.cotizando) ||
      (this.esPeru && this.cotizandoPe)
    );
  }

  /**
   * Tasa de REFERENCIA (moneda destino por 1 USD): la de mercado, SOLO para
   * mostrar — el envío lo calcula el backend. Distinta de `displayTasa`, que es
   * la efectiva (recibe ÷ envía) y por eso queda por debajo.
   *
   * CO y PE mandan `tasaReferencia{Cop,Pen}` aparte de la tasa con la que
   * calculan; hacemos fallback a esa última para los entornos que todavía no
   * exponen la referencia.
   */
  get displayTasaReferencia(): number | null {
    if (this.esColombia) {
      return this.cotizacion?.tasaReferenciaCop ?? this.cotizacion?.tasaCop ?? null;
    }
    if (this.esPeru) {
      return this.cotizacionPe?.tasaReferenciaPen ?? this.cotizacionPe?.tasaPen ?? null;
    }
    return this.estimate?.sellPrice ?? null;
  }

  /**
   * Costo total del envío en USD, medido contra la tasa de REFERENCIA:
   *   comisión = monto − recibe / tasaReferencia
   * Así el desglose cuadra con la tasa que se muestra —
   * `(monto − comisión) × tasaReferencia = recibe` — e incluye tanto el fee
   * fijo como el spread. Deriva de valores que ya vienen del backend: no
   * recalcula el envío.
   */
  get displayComisionUsd(): number | null {
    const tasa = this.displayTasaReferencia;
    const recibe = this.displayRecibe;
    if (!tasa || !recibe || !this.amountUsd) return null;
    return Math.max(0, this.amountUsd - recibe / tasa);
  }

  /**
   * Los railes USD→COP y USD→PEN llevan la tasa de referencia en la cabecera
   * del desglose (son los que la reciben del backend). En los demás destinos la
   * cabecera sigue mostrando la efectiva y la de referencia va como fila del
   * cuerpo.
   */
  get tasaReferenciaEnCabecera(): boolean {
    return (this.esColombia || this.esPeru) && this.puedeDesplegarDesglose;
  }

  /** Solo se puede abrir el desglose si hay cotización vigente con sus partes. */
  get puedeDesplegarDesglose(): boolean {
    return (
      !!this.amountUsd &&
      !this.calculandoUsdt &&
      !this.estimateError &&
      this.displayDisponible &&
      this.displayTasaReferencia != null &&
      this.displayComisionUsd != null
    );
  }

  /**
   * Fecha estimada de acreditación (dd/MM) = hoy + `displayDias` días hábiles.
   * Sin calendario de feriados: es una estimación, igual que el plazo que
   * informa el proveedor.
   */
  get displayAcreditacion(): string {
    const f = new Date();
    let restantes = Math.max(0, this.displayDias);
    while (restantes > 0) {
      f.setDate(f.getDate() + 1);
      const dow = f.getDay();
      if (dow !== 0 && dow !== 6) restantes--;
    }
    const dd = String(f.getDate()).padStart(2, '0');
    const mm = String(f.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}`;
  }

  /**
   * El monto a enviar supera el saldo USDT del cliente. Permite SIMULAR la
   * cotización pero bloquea el avance al envío (botón "Continuar" disabled).
   * Si el saldo aún no cargó (`null`), no bloqueamos — el backend re-valida.
   */
  get saldoInsuficiente(): boolean {
    return this.saldoUsdt != null && this.amountUsd != null && this.amountUsd > this.saldoUsdt;
  }

  /** Mínimo en USD del destino elegido (nunca menor a `REMESA_MIN_USD`). */
  get minUsdVigente(): number {
    return Math.max(REMESA_MIN_USD, this.selectedVariant?.minAmountUsd || REMESA_MIN_USD);
  }

  /** Falta saldo USDT. Mismos dos casos que `faltaSaldoClp`. */
  get faltaSaldoUsdt(): boolean {
    if (this.saldoUsdt == null) return false;
    return this.saldoInsuficiente || this.saldoUsdt < this.minUsdVigente;
  }

  /**
   * Recarga de la moneda con la que se está pagando el envío.
   *
   * Mismo criterio que el home del cliente (`user-home`): las fiat —CLP y
   * VES— se recargan en `rechargeAccount`, que ya bifurca sus cuentas por
   * `tipoId`, y USDT en `load-pays-usdt`. Sin esto el aviso de saldo era una
   * pared: decía "recargá" sin decir dónde.
   */
  irARecargar(): void {
    const ruta =
      this.origen === 'USDT'
        ? '/wallet/client/load-pays-usdt'
        : '/wallet/client/rechargeAccount';
    this.router.navigate([ruta]);
  }

  /**
   * El monto supera el máximo por envío (REMESA_MAX_USD). Igual que el saldo:
   * se simula pero NO se avanza al envío. El máximo es tope duro del backend.
   */
  get montoExcedeMaximo(): boolean {
    return this.amountUsd != null && this.amountUsd > REMESA_MAX_USD;
  }

  /**
   * Bajo el mínimo (20 USD, o el mínimo del destino si es mayor). Se cotiza
   * igual (el cliente ve cuánto recibiría) pero NO se puede enviar.
   */
  get montoBajoMinimo(): boolean {
    if (this.amountUsd == null || this.amountUsd <= 0) return false;
    const min = Math.max(REMESA_MIN_USD, this.selectedVariant?.minAmountUsd || REMESA_MIN_USD);
    return this.amountUsd < min;
  }

  /**
   * Habilita "Continuar" SOLO cuando hay una conversión cargada y vigente para
   * los valores actuales. Exige que no haya nada en vuelo (`estimating` /
   * `cotizando`) — si no, durante el debounce del monto o la carga del rail el
   * botón quedaba activo con la cotización vieja en pantalla. Para Colombia
   * además requiere la cotización real disponible.
   */
  get puedeContinuar(): boolean {
    if (this.estimating || this.cotizando || this.cotizandoPe) return false;
    // La conversión en pantalla debe ser para el rail+monto actuales.
    if (this.cotizadoSnapshot !== this.snapshotInputs) return false;
    if (this.saldoInsuficiente || this.montoExcedeMaximo || this.montoBajoMinimo) return false;
    // CO/PE se apoyan en la cotización real (Modelo A); otros países, en el estimate.
    if (this.esColombia) return this.cotizacion?.disponible === true;
    if (this.esPeru) return this.cotizacionPe?.disponible === true;
    return !!this.estimate;
  }

  /** Firma de los inputs actuales que determinan la conversión. */
  private get snapshotInputs(): string {
    return `${this.selectedVariantCode}|${this.amountUsd}`;
  }

  // -------------------- Paso 2: Beneficiario dinámico --------------------

  goToBeneficiary(): void {
    // Misma fuente de verdad que el botón: no avanzar si la conversión no está
    // cargada/vigente, si supera el máximo o el saldo (solo se simula).
    if (!this.puedeContinuar) return;
    this.loadSchema(this.selectedVariantCode);
    this.loadBeneficiarios();
    this.step = 'beneficiary';
  }

  // -------------------- Libreta de beneficiarios --------------------

  /**
   * Lista los beneficiarios guardados del usuario para el PAÍS de envío
   * seleccionado. Deliberadamente no filtramos por variante — el mismo
   * tercero puede recibir por cualquier rail del país.
   */
  loadBeneficiarios(): void {
    const pais = this.selectedCountryCode;
    if (!pais) return;
    this.beneficiariosLoading = true;
    this.beneficiariosError = null;
    this.selectedBeneficiarioId = null;
    this.searchTxt = '';
    this.sub.add(
      this.beneficiarioSvc.list(pais).subscribe({
        next: (list) => {
          this.beneficiarios = list || [];
          this.beneficiariosLoading = false;
        },
        error: (err) => {
          this.beneficiariosLoading = false;
          this.beneficiariosError =
            typeof err === 'string' ? err : 'Error cargando beneficiarios';
        },
      }),
    );
  }

  selectBeneficiario(b: VitaBeneficiario): void {
    // Toggle: si ya estaba seleccionado, lo des-selecciona.
    if (this.selectedBeneficiarioId === b.id) {
      this.selectedBeneficiarioId = null;
      this.formValues = {};
      return;
    }
    // Elegir un beneficiario limpia cualquier aviso de bloqueo previo.
    this.bloqueoOperador = null;
    if (b.vitaEstado === 'banned') {
      // Bloqueado por cumplimiento → no se puede enviar; derivar a operador.
      this.selectedBeneficiarioId = null;
      this.formValues = {};
      this.bloqueoOperador = this.MSG_OP_BANNED;
      return;
    }
    this.selectedBeneficiarioId = b.id;
    // Hidratar formValues con identidad (columnas) + routing (JSON).
    const fv: Record<string, any> = {};
    for (const k of Object.keys(IDENTITY_MAP)) {
      const col = IDENTITY_MAP[k];
      fv[k] = (b as any)[col] ?? '';
    }
    for (const k of Object.keys(b.datosBancarios || {})) {
      fv[k] = b.datosBancarios[k];
    }
    this.formValues = fv;

    // Validar completitud por variante contra las reglas VIVAS de Vita; si
    // faltan datos, abrir el dialog para completarlos antes de continuar.
    this.verificarCompletitud(b.id);
  }

  /**
   * Chequea que el beneficiario cumpla las reglas vivas de su variante. Si
   * falta algo, NO se autocompleta: se des-selecciona y se deriva al usuario a
   * un operador de Aguacate (el cliente no edita datos incompletos por su
   * cuenta; aplica a empresa y persona).
   */
  private verificarCompletitud(id: string): void {
    this.sub.add(
      this.beneficiarioSvc.completitud(id).subscribe({
        next: (res) => {
          if (!res.valid && res.faltantes?.length) {
            this.selectedBeneficiarioId = null;
            this.formValues = {};
            this.bloqueoOperador = this.MSG_OP_BENEFICIARIO;
          }
        },
        error: () => {
          /* best-effort: la guardia del backend en el envío también corta */
        },
      }),
    );
  }

  get selectedBeneficiario(): VitaBeneficiario | null {
    return (
      this.beneficiarios.find((b) => b.id === this.selectedBeneficiarioId) ||
      null
    );
  }

  get filteredBeneficiarios(): VitaBeneficiario[] {
    // Vita publica variantes en minúsculas — normalizamos para comparar sin
    // sorpresas con lo que devuelve el catálogo (MAYÚSCULAS en el front).
    const variante = (this.selectedVariantCode || '').toLowerCase();
    const byVariant = this.beneficiarios.filter(
      (b) => !variante || (b.variante || '').toLowerCase() === variante,
    );
    const q = this.searchTxt.trim().toLowerCase();
    if (!q) return byVariant;
    return byVariant.filter((b) => {
      const hay = `${b.nombre} ${b.apellidos || ''} ${this.displayBanco(b)} ${b.numeroId}`.toLowerCase();
      return hay.includes(q);
    });
  }

  // ─── Helpers de display dinámicos para la card ───
  //
  // Derivan del JSON libre `datosBancarios` si los campos denormalizados
  // (bancoNombre / cuentaMask) están vacíos. Sigue el principio no-code:
  // no hardcodean keys por país, solo prueban un orden universal.

  /** Nombre visible del banco: col denormalizada → variante label → variante raw. */
  displayBanco(b: VitaBeneficiario): string {
    return b.bancoNombre || b.varianteLabel || (b.variante || '').toUpperCase();
  }

  /** Máscara de cuenta: col denormalizada → derivada del primer identificador
   *  que aparezca en `datosBancarios`. */
  displayCuentaMask(b: VitaBeneficiario): string {
    if (b.cuentaMask) return b.cuentaMask;
    const d = b.datosBancarios || {};
    for (const k of Object.keys(d).sort()) {
      const v = d[k];
      if (v === null || v === undefined || v === '') continue;
      const s = String(v);
      // Si es corto (select / tipo) saltar — buscamos un identificador.
      if (s.length < 4) continue;
      return '●●●●' + s.slice(-4);
    }
    return '—';
  }

  /** Nombre completo (persona natural o razón social). */
  displayNombre(b: VitaBeneficiario): string {
    const full = `${b.nombre || ''} ${b.apellidos || ''}`.trim();
    if (full) return full;
    // Fallback para beneficiarios Corporate cuyo nombre viene en datosBancarios.
    const companyName = (b.datosBancarios || {})['company_name'];
    if (companyName) return String(companyName);
    return '—';
  }

  /**
   * Modalidad discriminante del beneficiario — el valor que Vita usa en los
   * `when` para ramificar el form. Ej: VE "Pago movil" vs "Transferencia
   * bancaria".
   *
   * Principio no-code: NO hardcodeamos claves. Leemos el schema cargado en
   * vivo (`this.schema.fields`) y extraemos TODAS las `when.key` únicas —
   * esas son las "dimensiones" que Vita distingue para la variante actual.
   * Para cada una, mostramos el valor elegido (con su label humano si es
   * un `select`).
   *
   * Si el schema no trae `when`s, la variante no tiene ramificaciones y el
   * chip no se muestra.
   */
  displayModalidad(b: VitaBeneficiario): string | null {
    if (!this.schema) return null;
    const discriminators = new Set<string>();
    for (const f of this.schema.fields || []) {
      if (f.when?.key) discriminators.add(f.when.key);
    }
    if (discriminators.size === 0) return null;

    const d = b.datosBancarios || {};
    const parts: string[] = [];
    for (const key of discriminators) {
      const raw = d[key];
      if (raw === undefined || raw === null || raw === '') continue;
      parts.push(this.labelForDiscriminator(key, raw));
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }

  /**
   * Convierte el valor crudo de un discriminador en su label si el campo es
   * un select y tiene options. Sino, devuelve el valor tal cual.
   */
  private labelForDiscriminator(key: string, value: any): string {
    const field = (this.schema?.fields || []).find((f) => f.key === key);
    if (field?.options) {
      const opt = field.options.find((o) => String(o.value) === String(value));
      if (opt?.label) return opt.label;
    }
    return String(value);
  }

  /**
   * Abre el modal de detalle (solo lectura + acción "Usar este").
   * Ojo con `event.stopPropagation()` en el template: evita que el click en
   * el botón dispare también el `selectBeneficiario` del card padre.
   */
  openDetail(b: VitaBeneficiario, event?: Event): void {
    event?.stopPropagation();
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(VitaBeneficiarioDetailDialogComponent, {
      data: { beneficiario: b },
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '480px',
      maxHeight: '90vh',
    });
    this.sub.add(
      ref.afterClosed().subscribe((result: VitaBeneficiarioDetailResult) => {
        if (result === 'use' && b.vitaEstado !== 'banned') {
          this.selectedBeneficiarioId = null; // forzar toggle a true
          this.selectBeneficiario(b);
        }
      }),
    );
  }

  /**
   * Abre el dialog de creación de beneficiario. Al cerrar con un resultado,
   * refresca la libreta y auto-selecciona el nuevo.
   */
  openAddBeneficiarioDialog(): void {
    if (!this.schema || !this.selectedCountryCode || !this.selectedVariantCode) {
      return;
    }
    const isMobile = window.innerWidth < 768;
    const ref = this.dialog.open(AddVitaBeneficiarioDialogComponent, {
      data: {
        countryCode: this.selectedCountryCode,
        variant: this.selectedVariantCode,
        variantLabel: this.selectedVariant?.label || this.selectedVariantCode,
        schema: this.schema,
      },
      disableClose: true,
      panelClass: 'aguacate-dialog',
      maxWidth: isMobile ? '95vw' : '560px',
      minWidth: isMobile ? '90vw' : '500px',
      maxHeight: '90vh',
    });

    this.sub.add(
      ref.afterClosed().subscribe((result: VitaBeneficiario | null) => {
        if (!result) return;
        // Insertarlo al tope y auto-seleccionarlo.
        this.beneficiarios = [
          result,
          ...this.beneficiarios.filter((b) => b.id !== result.id),
        ];
        this.selectBeneficiario(result);
      }),
    );
  }

  /**
   * Abre el dialog en modo EDICIÓN: carga el schema de la variante del
   * beneficiario, pre-llena con sus datos actuales y, al guardar, re-valida
   * (backend) y refresca la libreta.
   */
  openEditBeneficiarioDialog(b: VitaBeneficiario, event?: Event): void {
    event?.stopPropagation();
    this.sub.add(
      this.calc.getRules(b.variante).subscribe({
        next: (schema) => {
          const initialValues: Record<string, any> = {};
          for (const k of Object.keys(IDENTITY_MAP)) {
            initialValues[k] = (b as any)[IDENTITY_MAP[k]] ?? '';
          }
          for (const k of Object.keys(b.datosBancarios || {})) {
            initialValues[k] = b.datosBancarios[k];
          }
          const isMobile = window.innerWidth < 768;
          const ref = this.dialog.open(AddVitaBeneficiarioDialogComponent, {
            data: {
              countryCode: b.paisCodigo,
              variant: b.variante,
              variantLabel: b.varianteLabel || b.variante,
              schema,
              editId: b.id,
              initialValues,
            },
            disableClose: true,
            panelClass: 'aguacate-dialog',
            maxWidth: isMobile ? '95vw' : '560px',
            minWidth: isMobile ? '90vw' : '500px',
            maxHeight: '90vh',
          });
          this.sub.add(
            ref.afterClosed().subscribe((result: VitaBeneficiario | null) => {
              if (!result) return;
              this.beneficiarios = this.beneficiarios.map((x) =>
                x.id === result.id ? result : x,
              );
              // Si estaba seleccionado, re-hidratar el form con lo editado.
              if (this.selectedBeneficiarioId === result.id) {
                const fv: Record<string, any> = {};
                for (const k of Object.keys(IDENTITY_MAP)) {
                  fv[k] = (result as any)[IDENTITY_MAP[k]] ?? '';
                }
                for (const k of Object.keys(result.datosBancarios || {})) {
                  fv[k] = result.datosBancarios[k];
                }
                this.formValues = fv;
              }
            }),
          );
        },
        error: () => {
          /* si no hay schema, no se puede editar dinámicamente */
        },
      }),
    );
  }

  loadSchema(variant: string): void {
    this.schemaLoading = true;
    this.schemaError = null;
    this.schema = null;
    this.formValues = {};
    this.formErrors = {};
    this.sub.add(
      this.calc.getRules(variant).subscribe({
        next: (res) => {
          this.schema = res;
          // Inicializar values con empty o primer option para selects
          for (const f of res.fields) {
            this.formValues[f.key] = '';
          }
          this.schemaLoading = false;
        },
        error: (err) => {
          this.schemaError =
            typeof err === 'string' ? err : 'Error cargando formulario';
          this.schemaLoading = false;
        },
      }),
    );
  }

  /**
   * Match de regla de visibilidad de Vita. `value` puede ser string o array
   * (ej. un banco VE que aplica a "Pago movil" y a "Transferencia bancaria").
   */
  private matchesRule(
    rule: CalculatorVisibilityRule | undefined,
    values: Record<string, any>,
  ): boolean {
    if (!rule) return true;
    const current = String(values?.[rule.key] ?? '');
    const expected = Array.isArray(rule.value) ? rule.value : [rule.value];
    return expected.some((v) => String(v) === current);
  }

  isOptionVisible(opt: CalculatorFormOption): boolean {
    return this.matchesRule(opt.visible, this.formValues);
  }

  /**
   * Visibilidad del field siguiendo el contrato dinámico de Vita:
   *  - Si las options traen `visible`, esa es la verdad (ignoramos `when`
   *    a nivel field, que Vita a veces deja desactualizado — caso VEVES
   *    `bank_code`).
   *  - Si no, cae al `when` del field.
   */
  isFieldVisible(field: CalculatorFormField): boolean {
    const hasOptionVisibility =
      field.type === 'select' &&
      !!field.options &&
      field.options.some((o) => !!o.visible);
    if (hasOptionVisibility) {
      return (field.options || []).some((o) => this.isOptionVisible(o));
    }
    return this.matchesRule(field.when, this.formValues);
  }

  /** Label legible del banco seleccionado (si aplica). */
  labelForValue(field: CalculatorFormField, value: any): string {
    if (!field.options) return String(value ?? '');
    const opt = field.options.find((o) => String(o.value) === String(value));
    return opt ? opt.label : String(value ?? '');
  }

  /**
   * Valida el form contra las reglas que publica Vita en `/withdrawal_rules`.
   * Vita envía: required (por estar visible), min/max length, tipo, options.
   * No inventamos reglas propias — sólo validamos lo que Vita pide.
   */
  validateForm(): boolean {
    this.formErrors = {};
    if (!this.schema) return false;

    for (const f of this.schema.fields) {
      if (!this.isFieldVisible(f)) continue;
      const err = this.validateFieldValue(f);
      if (err) this.formErrors[f.key] = err;
    }
    return Object.keys(this.formErrors).length === 0;
  }

  /** Valida un campo individual según las reglas Vita. Devuelve mensaje de error o null. */
  private validateFieldValue(field: CalculatorFormField): string | null {
    // Trim automático in-place
    const raw = this.formValues[field.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (typeof raw === 'string' && raw !== v) this.formValues[field.key] = v;

    // Requerido (todo field visible es requerido según Vita)
    if (v === undefined || v === null || v === '') {
      return 'Campo requerido';
    }

    // Longitud min/max — Vita solo lo aplica a text/email
    if ((field.type === 'text' || field.type === 'email') && typeof v === 'string') {
      if (field.min && v.length < field.min) {
        return `Mínimo ${field.min} caracteres`;
      }
      if (field.max && v.length > field.max) {
        return `Máximo ${field.max} caracteres`;
      }
    }

    // Email — formato estándar
    if (field.type === 'email' && typeof v === 'string') {
      if (!EMAIL_RE.test(v)) {
        return 'Formato de email inválido';
      }
    }

    // Select — valor debe pertenecer a las options visibles (una option
    // oculta por `visible` no cuenta aunque siga en field.options).
    if (field.type === 'select' && field.options) {
      const visible = field.options.filter((o) => this.isOptionVisible(o));
      const valid = visible.some((o) => String(o.value) === String(v));
      if (!valid) return 'Seleccione una opción válida';
    }

    return null;
  }

  /**
   * Hint bajo el input. Solo se muestra info que Vita realmente publica:
   * límites de longitud para text/email, ejemplo para email.
   */
  hintForField(field: CalculatorFormField): string | null {
    if (field.type === 'email') return 'Ej: nombre@dominio.com';
    if (field.type === 'text') {
      if (field.min && field.max) return `${field.min}–${field.max} caracteres`;
      if (field.min) return `Mínimo ${field.min} caracteres`;
      if (field.max) return `Máximo ${field.max} caracteres`;
    }
    return null;
  }

  /** Feedback inmediato al salir del campo (sin marcar vacíos hasta submit). */
  onFieldBlur(field: CalculatorFormField): void {
    delete this.formErrors[field.key];
    const raw = this.formValues[field.key];
    const v = typeof raw === 'string' ? raw.trim() : raw;
    if (typeof raw === 'string' && raw !== v) this.formValues[field.key] = v;
    // No marcamos vacío al blur (solo al submit)
    if (v === '' || v === undefined || v === null) return;
    const err = this.validateFieldValue(field);
    if (err && err !== 'Campo requerido') {
      this.formErrors[field.key] = err;
    }
  }

  /** Label legible de una moneda (ej. PEN → "Soles"). */
  currencyLabel(code: string): string {
    const upper = (code || '').toUpperCase();
    const name = CURRENCY_NAMES[upper];
    return name ? `${name} (${upper})` : upper;
  }

  /** Nombre local de la moneda sin paréntesis. */
  currencyName(code: string): string {
    const upper = (code || '').toUpperCase();
    return CURRENCY_NAMES[upper] || upper;
  }

  goToSummary(): void {
    if (!this.selectedBeneficiarioId) return; // UI ya bloquea el botón
    this.envioResult = null;
    this.envioError = null;
    this.bloqueoOperador = null;
    this.valoresCambiaron = false;
    this.step = 'summary';
  }

  // -------------------- Envío productivo USDT→COP (solo Colombia) --------------------

  /** El envío productivo hoy aplica solo al rail Colombia (USDT→COP). */
  get esColombia(): boolean {
    return (this.selectedCountryCode || '').toUpperCase() === 'CO';
  }

  /** Destino Perú (USD→PEN): envío productivo espejo de CO, moneda PEN (2 dec.). */
  get esPeru(): boolean {
    return (this.selectedCountryCode || '').toUpperCase() === 'PE';
  }

  /** Formato del pipe `number` para el RECIBE según moneda destino (COP entero, PEN 2 dec.). */
  get fmtRecibe(): string {
    return this.esPeru || this.esVesPeFlow || this.esClpPeFlow ? '1.2-2' : '1.0-0';
  }

  /**
   * Formato del pipe para la TASA.
   *
   * Depende de la MAGNITUD de la tasa, no sólo de la moneda destino: "COP por
   * USD" son miles (3120 → entero está bien), pero "COP por CLP" o "COP por VES"
   * viven en unidades (3,269) y con `'1.0-0'` se mostraban como **"3"**, que es
   * lo que veía el operador en el Resumen. Los orígenes locales llevan decimales.
   */
  get fmtTasa(): string {
    if (this.esPeru || this.esVesPeFlow || this.esClpPeFlow) return '1.2-4';
    if (this.esClpFlow || this.esVesFlow) return '1.2-4';
    return '1.0-0';
  }

  /** Mapea la variante seleccionada al riel del backend (COBREB | CO). */
  get remesaRail(): RemesaCoRail {
    return (this.selectedVariantCode || '').toUpperCase() === 'COBREB'
      ? 'COBREB'
      : 'CO';
  }

  /**
   * Crea la remesa real contra el backend (`POST /colombia/remesa-co`).
   * El backend re-valida margen y saldo; si la operación da pérdida responde
   * "Servicio no disponible, intente más tarde". La confirmación de Vita llega
   * async → la transacción queda POR_PROCESAR.
   *
   * Manda `requestId` (idempotencia WS1.1: un retry de red NO duplica) y, si
   * el cliente ya confirmó el aviso de duplicado, `confirmarDuplicado` (WS1.2).
   */
  confirmarEnvio(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResult) return;
    // CO ya NO depende de `estimate` (Vita) — su fuente de verdad es la
    // cotización Modelo A. Exigir `estimate` acá frenaba el envío (regresión).
    if (!this.esColombia || !this.selectedBeneficiarioId || !this.cotizacion?.disponible) return;

    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.othersPayouts
        .enviarRemesaCo({
          rail: this.remesaRail,
          montoUsd: this.amountUsd!,
          beneficiarioId: this.selectedBeneficiarioId,
          // Price-check: mandamos el COP que el cliente está viendo.
          expectedRecibeCop: this.cotizacion?.recibeCop,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResult = res;
            this.enviando = false;
            // Intención consumida → la próxima es OTRA (no replay de esta).
            this.requestId = nuevoRequestId();
            // El débito backend es atómico: reflejar el saldo nuevo ya.
            this.cargarSaldoUsdt();
            // Pantalla de resultado: NO volver al form relleno (anti re-envío).
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            // La tasa cambió → ajustamos los valores mostrados y avisamos.
            // NO se creó ninguna operación; el cliente re-confirma (mismo requestId).
            if (err && (err as ValoresCambiaron).cambioValores) {
              const v = err as ValoresCambiaron;
              this.cotizacion = {
                disponible: true,
                rail: this.remesaRail,
                montoUsd: this.amountUsd!,
                recibeCop: v.recibeCop,
                tasaCop: v.tasaCop,
                businessDays: v.businessDays,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  /**
   * Crea la remesa real USD→PEN (`POST /peru/remesa-pe`). Espejo de
   * `confirmarEnvio` (CO) para destino Perú, moneda PEN. Mismo manejo de errores
   * accionables + idempotencia (WS1.1). El backend re-valida margen/saldo.
   */
  confirmarEnvioPe(confirmandoDuplicado = false): void {
    if (this.enviando || this.envioResultPe) return;
    if (!this.esPeru || !this.selectedBeneficiarioId || this.cotizacionPe?.disponible !== true) return;

    this.enviando = true;
    this.envioError = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    this.sub.add(
      this.peruPayouts
        .enviarRemesa({
          montoUsd: this.amountUsd!,
          beneficiarioId: this.selectedBeneficiarioId,
          // Price-check: mandamos el PEN que el cliente está viendo.
          expectedRecibePen: this.cotizacionPe?.recibePen,
          requestId: this.requestId,
          ...(confirmandoDuplicado ? { confirmarDuplicado: true } : {}),
        })
        .subscribe({
          next: (res) => {
            this.envioResultPe = res;
            this.enviando = false;
            this.requestId = nuevoRequestId();
            this.cargarSaldoUsdt();
            this.step = 'done';
          },
          error: (err) => {
            this.enviando = false;
            if (err && (err as ValoresCambiaronPe).cambioValores) {
              const v = err as ValoresCambiaronPe;
              this.cotizacionPe = {
                disponible: true,
                rail: 'PE',
                montoUsd: this.amountUsd!,
                recibePen: v.recibePen,
                tasaPen: v.tasaPen,
                businessDays: v.businessDays,
              };
              this.valoresCambiaron = true;
              return;
            }
            if (esErrorAccionable(err)) {
              this.manejarErrorAccionable(err as unknown as RemesaCoErrorAccionable);
              return;
            }
            this.envioError =
              typeof err === 'string' ? err : 'No se pudo procesar el envío';
          },
        }),
    );
  }

  /** UX propia por código de error del backend (WS2.2 — nada de genéricos). */
  private manejarErrorAccionable(err: RemesaCoErrorAccionable): void {
    switch (err.tipo) {
      // Datos del EMISOR incompletos: el cliente NO autocompleta; se lo deriva
      // a un operador de Aguacate (mismo trato para empresa y persona).
      case 'REMITENTE_DATOS_INCOMPLETOS':
        this.bloqueoOperador = this.MSG_OP_REMITENTE;
        return;

      // Datos del BENEFICIARIO incompletos para la variante: idem, a operador.
      case 'DATOS_INCOMPLETOS':
        this.bloqueoOperador = this.MSG_OP_BENEFICIARIO;
        return;

      // Ya hay un envío igual en curso: confirmación EXPLÍCITA antes de duplicar.
      case 'POSIBLE_DUPLICADO':
        this.confirmarDuplicadoDialog(err);
        return;
    }
  }

  /** Modal "Ya tenés un envío igual" (WS2.2): ver estado o enviar igual. */
  private confirmarDuplicadoDialog(err: RemesaCoErrorAccionable): void {
    const p = err.previa;
    // Cada origen muestra su moneda (el cliente nunca ve USD salvo flujo USDT):
    // VES→COP en VES, CLP→COP en CLP, USDT→COP en USD.
    const montoPrevia = p
      ? this.esVesFlow || this.esVesPeFlow
        ? `${Math.round(Number(p.montoVes) || 0).toLocaleString('es-VE')} VES`
        : this.esClpFlow
        ? `${Math.round(Number(p.montoClp) || 0).toLocaleString('es-CL')} CLP`
        : `${Number(p.montoUsd || 0).toFixed(2)} USD`
      : '';
    const detalle = p
      ? `<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:12px;padding:12px 14px;margin-top:10px;text-align:left;font-size:0.85rem;color:#374151;">
           <div><b>Operación:</b> #${p.txId}</div>
           <div><b>Monto:</b> ${montoPrevia}</div>
           <div><b>Estado:</b> ${p.enVuelo ? 'En proceso' : 'Completada hace minutos'}</div>
         </div>`
      : '';
    Swal.fire({
      icon: 'warning',
      title: '¿Enviar de nuevo?',
      html: `<p style="font-size:0.9rem;color:#374151;margin:0;">Ya tenés un envío <b>igual a este</b> (mismo beneficiario y monto)${p?.enVuelo ? ' <b>en curso</b>' : ' reciente'}.</p>${detalle}
        <p style="font-size:0.8rem;color:#6b7280;margin-top:10px;">Si fue un reintento, no hace falta enviarlo otra vez.</p>`,
      showCancelButton: true,
      confirmButtonText: 'Enviar igual',
      cancelButtonText: 'No, ir al inicio',
      confirmButtonColor: '#1C593D',
      cancelButtonColor: '#6b7280',
      reverseButtons: true,
    }).then((r) => {
      if (r.isConfirmed) {
        // Intención NUEVA y deliberada → requestId fresco (si se reusara, el
        // replay idempotente devolvería la remesa anterior y no enviaría nada).
        this.requestId = nuevoRequestId();
        if (this.esVesPeFlow) this.confirmarEnvioVesPe(true);
        else if (this.esClpPeFlow) this.confirmarEnvioClpPe(true);
        else if (this.esVesFlow) this.confirmarEnvioVes(true);
        else if (this.esClpFlow) this.confirmarEnvioClp(true);
        else if (this.esPeru) this.confirmarEnvioPe(true);
        else this.confirmarEnvio(true);
      } else if (r.dismiss === Swal.DismissReason.cancel) {
        // Al inicio (el historial del cliente vive ahí; /wallet/orders no
        // es una ruta del cliente).
        this.router.navigate(['/wallet/home']);
      }
    });
  }

  backToCalc(): void {
    this.limpiarBannersEnvio();
    this.step = 'calc';
  }

  backToBeneficiary(): void {
    this.limpiarBannersEnvio();
    this.step = 'beneficiary';
  }

  /**
   * Limpia los banners de resultado del envío (error transitorio, bloqueo a
   * operador y "valores cambiaron") al navegar hacia atrás. Sin esto, un banner
   * de un intento previo queda "pegado" al volver al paso beneficiario/calc.
   */
  private limpiarBannersEnvio(): void {
    this.envioError = null;
    this.bloqueoOperador = null;
    this.valoresCambiaron = false;
  }

  restart(): void {
    this.step = 'calc';
    this.selectedCountryCode = '';
    this.selectedVariantCode = '';
    this.variants = [];
    this.amountUsd = 20;
    this.montoClp = 20000;
    this.cotizacionClp = null;
    this.cotizacionClpPe = null;
    this.envioResultClpPe = null;
    this.clpDestino = '';
    this.montoVes = 20000;
    this.cotizacionVes = null;
    this.envioResultVes = null;
    this.cotizacionVesPe = null;
    this.envioResultVesPe = null;
    this.vesDestino = '';
    this.estimate = null;
    this.schema = null;
    this.formValues = {};
    this.formErrors = {};
    this.estimateError = null;
    this.beneficiarios = [];
    this.selectedBeneficiarioId = null;
    this.searchTxt = '';
    this.enviando = false;
    this.envioResult = null;
    this.envioResultClp = null;
    this.envioError = null;
    this.cotizacion = null;
    this.cotizando = false;
    this.cotizacionPe = null;
    this.cotizandoPe = false;
    this.envioResultPe = null;
    this.cotizadoSnapshot = null;
    this.valoresCambiaron = false;
    this.bloqueoOperador = null;
    // Intención nueva (defensa extra; el éxito ya regeneró).
    this.requestId = nuevoRequestId();
    this.cargarSaldoUsdt();
  }

  /** PNG local de la bandera del país, o null si no hay asset. */
  flagSrc(c: CalculatorCountry): string | null {
    return FLAG_ASSETS[(c.code || '').toUpperCase()] ?? null;
  }

  /** Selección de país por botones — reemplaza el dropdown. */
  selectCountry(code: string): void {
    if (this.selectedCountryCode === code) return;
    this.selectedCountryCode = code;
    this.onCountryChange();
  }

  /** Selección de rail por botones (WS2.3) — reemplaza el dropdown. */
  selectVariant(code: string): void {
    if (this.selectedVariantCode === code) return;
    this.selectedVariantCode = code;
    this.onVariantChange();
  }

  /** Plazo corto para el botón de rail ("Instantáneo" / "1 día hábil"). */
  variantPlazoCorto(v: CalculatorVariant): string {
    // El rail PEN de Perú es INSTANTÁNEO; Vita reporta 1 día por error → override.
    if (this.esPeru && (v.code || '').toLowerCase() === 'pe') return 'Instantáneo';
    const d = Number(v.businessDays) || 0;
    if (d <= 0) return 'Instantáneo';
    return d === 1 ? '1 día hábil' : `${d} días hábiles`;
  }

  // -------------------- Getters útiles para template --------------------

  get selectedCountry(): CalculatorCountry | null {
    return this.countries.find((c) => c.code === this.selectedCountryCode) || null;
  }

  get selectedVariant(): CalculatorVariant | null {
    return (
      this.variants.find((v) => v.code === this.selectedVariantCode) || null
    );
  }

  get effectiveMinAmount(): number {
    return Math.max(20, this.selectedVariant?.minAmountUsd || 0);
  }

  get visibleSchemaFields(): CalculatorFormField[] {
    // En el resumen mostramos SOLO fields del beneficiario. Excluimos los
    // del emisor/contexto (fc_*, purpose, beneficiary_type) — esos no
    // corresponden al beneficiario guardado y quedarían con "—".
    return (
      this.schema?.fields
        .filter((f) => isBeneficiaryField(f.key))
        .filter((f) => this.isFieldVisible(f)) || []
    );
  }

  get freshnessLabel(): string {
    if (!this.selectedVariant?.validUntil) return '';
    const minsLeft = Math.floor(
      (new Date(this.selectedVariant.validUntil).getTime() - Date.now()) / 60000,
    );
    if (minsLeft < 0) return `expiró hace ${Math.abs(minsLeft)}m`;
    if (minsLeft === 0) return `expira en < 1m`;
    return `válido por ${minsLeft}m`;
  }

  get freshnessClass(): 'fresh' | 'warn' | 'expired' {
    if (!this.selectedVariant?.validUntil) return 'warn';
    const minsLeft =
      (new Date(this.selectedVariant.validUntil).getTime() - Date.now()) /
      60000;
    if (minsLeft < 0) return 'expired';
    if (minsLeft < 3) return 'warn';
    return 'fresh';
  }
}
